import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Activity, RefreshCw, X, AlertTriangle, Cpu, Scissors, Mic, Sparkles, Crosshair, Minimize2, Video } from "lucide-react";

const KIND_META = {
  ai_cut:      { icon: Cpu,        label: "AI Cuts",         color: "text-blue-400" },
  clip_gen:    { icon: Scissors,   label: "Generate Clips",  color: "text-orange-400" },
  audio_id:    { icon: Mic,        label: "Audio Rider ID",  color: "text-emerald-400" },
  rider_track: { icon: Crosshair,  label: "Track Rider",     color: "text-purple-400" },
  reel:        { icon: Sparkles,   label: "Reel",            color: "text-yellow-400" },
  compress:    { icon: Minimize2,  label: "Compress",        color: "text-rose-400" },
  vimeo:       { icon: Video,      label: "Vimeo",           color: "text-sky-400" },
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [killing, setKilling] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/jobs", { params: { active_only: !showAll } });
      setJobs(r.data.jobs || []);
    } catch (e) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [showAll]);
  useEffect(() => {
    const id = setInterval(refresh, 5000); // auto-refresh
    return () => clearInterval(id);
  }, [showAll]);

  const kill = async (job) => {
    if (!window.confirm(`Kill "${job.name}"?\n\nThe job will be marked failed. Any running subprocess may keep working for a moment until it notices.`)) return;
    setKilling(job.kind + ":" + job.id);
    try {
      await api.post(`/jobs/${job.kind}/${job.id}/kill`);
      toast.success("Job killed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to kill");
    } finally {
      setKilling(null);
    }
  };

  const active = jobs.filter((j) => j.killable);
  const recent = jobs.filter((j) => !j.killable);

  const counts = active.reduce((m, j) => ({ ...m, [j.kind]: (m[j.kind] || 0) + 1 }), {});

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1400px]" data-testid="jobs-page">
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">System Monitor</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Background Jobs</h1>
          <p className="text-stone-400 mt-3 max-w-xl">
            Every AI cut, audio ID, clip generation, reel render, and compression. Kill anything stuck before it eats your RAM.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAll((v) => !v)}
            className={`btn-secondary inline-flex items-center gap-1.5 text-xs ${showAll ? "!border-orange-600 !text-orange-400" : ""}`}
            data-testid="jobs-toggle-all"
          >
            <Activity size={12} /> {showAll ? "Showing all" : "Active only"}
          </button>
          <button onClick={refresh} className="btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="jobs-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {Object.entries(KIND_META).map(([kind, meta]) => (
          <div key={kind} className="border border-stone-800 p-3" data-testid={`jobs-stat-${kind}`}>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${meta.color}`}>
              <meta.icon size={11} /> {meta.label}
            </div>
            <div className="font-display font-bold text-2xl mt-1">{counts[kind] || 0}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : (
        <>
          <JobList
            title={`Running (${active.length})`}
            jobs={active}
            empty="Nothing running. Quiet on the range."
            onKill={kill}
            killing={killing}
          />
          {showAll && (
            <JobList
              title={`Recent (${recent.length})`}
              jobs={recent}
              empty="No recent jobs."
              onKill={null}
            />
          )}
        </>
      )}
    </div>
  );
}

function JobList({ title, jobs, empty, onKill, killing }) {
  return (
    <section className="mb-10" data-testid="job-section">
      <h2 className="font-display uppercase text-lg font-bold mb-3">{title}</h2>
      {jobs.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500 text-sm">{empty}</div>
      ) : (
        <div className="border border-stone-800">
          <div className="hidden md:grid grid-cols-[140px_1fr_140px_100px_110px] gap-3 px-4 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500">
            <div>Kind</div><div>Name</div><div>Status</div><div className="text-right">Progress</div><div></div>
          </div>
          {jobs.map((j) => <JobRow key={j.kind + ":" + j.id} job={j} onKill={onKill} killing={killing} />)}
        </div>
      )}
    </section>
  );
}

function JobRow({ job, onKill, killing }) {
  const meta = KIND_META[job.kind] || { icon: Activity, label: job.kind, color: "text-stone-400" };
  const Icon = meta.icon;
  const isKilling = killing === job.kind + ":" + job.id;
  const statusBadge = badgeFor(job.status);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_140px_100px_110px] gap-3 px-4 py-3 border-b border-stone-900 items-center" data-testid={`job-row-${job.kind}-${job.id}`}>
      <div className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest ${meta.color}`}>
        <Icon size={12} /> {meta.label}
      </div>
      <div className="min-w-0">
        <div className="text-sm truncate" title={job.name}>{job.name}</div>
        <div className="text-[10px] text-stone-500 font-mono-tc">{(job.created_at || "").replace("T", " ").slice(0, 19)} UTC</div>
        {job.error && (
          <div className="mt-1 inline-flex items-start gap-1 text-[11px] text-rose-400">
            <AlertTriangle size={10} className="mt-0.5 shrink-0" /> <span className="line-clamp-1">{job.error}</span>
          </div>
        )}
      </div>
      <div>
        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${statusBadge}`}>{job.status}</span>
      </div>
      <div className="text-right">
        {job.progress > 0 ? (
          <span className="font-mono-tc text-stone-300 text-sm">{job.progress}%</span>
        ) : <span className="text-stone-700 text-xs">—</span>}
      </div>
      <div className="text-right">
        {onKill && job.killable && (
          <button
            onClick={() => onKill(job)}
            disabled={isKilling}
            className="btn-secondary !py-1 !px-2 text-[11px] hover:!text-rose-400 inline-flex items-center gap-1"
            data-testid={`kill-${job.kind}-${job.id}`}
          >
            <X size={11} /> {isKilling ? "Killing…" : "Kill"}
          </button>
        )}
      </div>
    </div>
  );
}

function badgeFor(status) {
  if (status === "running" || status === "processing" || status === "encoding") return "border-orange-600 text-orange-400";
  if (status === "queued" || status === "downloading" || status === "uploading") return "border-blue-600 text-blue-400";
  if (status === "done") return "border-emerald-600 text-emerald-400";
  if (status === "failed") return "border-rose-600 text-rose-400";
  return "border-stone-700 text-stone-400";
}
